import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { api } from '@/utils/api';

interface Script {
    id: string;
    title: string;
    description?: string;
    lastEditedAt: string;
}

interface Member {
    userId: string;
    name: string;
    email: string;
    role: string;
}

export function OrgDashboard() {
    const { orgId } = useParams<{ orgId: string }>();
    const [scripts, setScripts] = useState<Script[]>([]);
    const [members, setMembers] = useState<Member[]>([]);
    const [org, setOrg] = useState<{ name: string } | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, [orgId]);

    const loadData = async () => {
        if (!orgId) return;

        try {
            const [orgData, scriptsData, membersData] = await Promise.all([
                api.getOrganization(orgId),
                api.getScripts({ organizationId: orgId }),
                api.getOrgMembers(orgId),
            ]);
            setOrg(orgData);
            setScripts(scriptsData);
            setMembers(membersData);
        } catch (error) {
            console.error('Failed to load data:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <Header />
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Loading...
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <Header />

            <main style={{ flex: 1, overflow: 'auto', padding: 'var(--spacing-2xl)' }}>
                <div className="container">
                    <div className="flex justify-between items-center" style={{ marginBottom: 'var(--spacing-xl)' }}>
                        <div>
                            <h1 style={{ marginBottom: 'var(--spacing-sm)' }}>{org?.name}</h1>
                            <p style={{ color: 'var(--color-text-secondary)', marginBottom: 0 }}>
                                Organization Dashboard
                            </p>
                        </div>
                        <div className="flex gap-md">
                            <Link to={`/org/${orgId}/admin`}>
                                <Button variant="secondary">Manage Members</Button>
                            </Link>
                            <Link to={`/org/${orgId}/settings`}>
                                <Button variant="secondary">Settings</Button>
                            </Link>
                        </div>
                    </div>

                    {/* Scripts Section */}
                    <section style={{ marginBottom: 'var(--spacing-2xl)' }}>
                        <h2 style={{ marginBottom: 'var(--spacing-lg)' }}>Scripts</h2>
                        {scripts.length === 0 ? (
                            <div className="panel" style={{ textAlign: 'center' }}>
                                <p style={{ marginBottom: 0 }}>No scripts in this organization yet.</p>
                            </div>
                        ) : (
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                    gap: 'var(--spacing-lg)',
                                }}
                            >
                                {scripts.map(script => (
                                    <Link key={script.id} to={`/editor/${script.id}`} style={{ textDecoration: 'none' }}>
                                        <Card>
                                            <h3 style={{ marginBottom: 'var(--spacing-sm)', color: 'var(--color-text-primary)' }}>
                                                {script.title}
                                            </h3>
                                            {script.description && (
                                                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-sm)' }}>
                                                    {script.description}
                                                </p>
                                            )}
                                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>
                                                Last edited: {new Date(script.lastEditedAt).toLocaleDateString()}
                                            </div>
                                        </Card>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Members Section */}
                    <section>
                        <h2 style={{ marginBottom: 'var(--spacing-lg)' }}>Members ({members.length})</h2>
                        <div className="panel">
                            <div className="flex flex-col gap-md">
                                {members.slice(0, 5).map(member => (
                                    <div key={member.userId} className="flex justify-between items-center">
                                        <div>
                                            <div style={{ fontWeight: 500 }}>{member.name}</div>
                                            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                                                {member.email}
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                fontSize: '0.875rem',
                                                color: 'var(--color-text-tertiary)',
                                                textTransform: 'capitalize',
                                            }}
                                        >
                                            {member.role}
                                        </div>
                                    </div>
                                ))}
                                {members.length > 5 && (
                                    <Link to={`/org/${orgId}/admin`} style={{ textAlign: 'center', marginTop: 'var(--spacing-sm)' }}>
                                        View all members →
                                    </Link>
                                )}
                            </div>
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
}
